//----------------------------------------------------------------------------------------------------------------------
// Value Binding
//----------------------------------------------------------------------------------------------------------------------

import type { GameEventBus } from '../eventBus.ts';

// Interfaces
import type { Action } from '../../interfaces/action.ts';
import type { Binding, ValueBindingDefinition } from '../../interfaces/binding.ts';
import type { DeviceType, DeviceValueReader, InputState } from '../../interfaces/input.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Options for configuring a value binding
 */
export interface ValueBindingOptions
{
    /**
     * Factor to scale the input value by (1.0 = no scaling)
     */
    scale ?: number;

    /**
     * Value to add to the input value after scaling
     */
    offset ?: number;

    /**
     * Whether to invert the input value (multiply by -1)
     */
    invert ?: boolean;

    /**
     * Optional context name this binding belongs to
     */
    context ?: string;

    /**
     * If true, only emit values when they change
     * If false, always emit values on each process call
     */
    emitOnChange ?: boolean;

    /**
     * Deadzone threshold (0.0 to 1.0)
     * Input values below this threshold will be treated as 0
     */
    deadzone ?: number;

    /**
     * Value to emit when the input is digital and true
     * Only used when action is digital
     */
    onValue ?: boolean | number;

    /**
     * Value to emit when the input is digital and false
     * Only used when action is digital
     */
    offValue ?: boolean | number;

    /**
     * Minimum value to clamp output to
     */
    min ?: number;

    /**
     * Maximum value to clamp output to
     */
    max ?: number;
}

/**
 * Implementation of a value binding, which directly converts an analog input
 * to an analog output with optional scaling, offset, and clamping.
 * Can handle both digital and analog inputs/outputs based on action type.
 */
export class ValueBinding implements Binding
{
    public readonly type = 'value' as const;
    public readonly action : Action;
    public readonly context ?: string;
    public readonly deviceID : string;
    public readonly deviceType : DeviceType;
    public readonly reader : DeviceValueReader;

    // Value-specific options
    private readonly _scale : number;
    private readonly _offset : number;
    private readonly _invert : boolean;
    private readonly _emitOnChange : boolean;
    private readonly _deadzone : number;
    private readonly _onValue : boolean | number;
    private readonly _offValue : boolean | number;
    private readonly _min : number;
    private readonly _max : number;

    // State tracking
    private _lastValue : number | boolean | undefined;

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Get the current options for this value binding.
     */
    get options() : ValueBindingOptions
    {
        return {
            scale: this._scale,
            offset: this._offset,
            invert: this._invert,
            emitOnChange: this._emitOnChange,
            deadzone: this._deadzone,
            onValue: this._onValue,
            offValue: this._offValue,
            min: this._min,
            max: this._max,
        };
    }

    //------------------------------------------------------------------------------------------------------------------

    /**
     * Create a new value binding
     *
     * @param action
     * @param deviceID
     * @param reader
     * @param options
     */
    constructor(
        action : Action,
        deviceID : string,
        deviceType : DeviceType,
        reader : DeviceValueReader,
        options : ValueBindingOptions = {}
    )
    {
        this.action = action;
        this.deviceID = deviceID;
        this.deviceType = deviceType;
        this.reader = reader;
        this.context = options.context;

        // Common options that apply to both analog and digital
        this._scale = options.scale ?? 1.0;
        this._offset = options.offset ?? 0.0;
        this._invert = options.invert ?? false;
        this._emitOnChange = options.emitOnChange ?? true;
        this._deadzone = options.deadzone ?? 0.0;

        // For digital actions, these control the output value
        // For analog actions, these are not used
        this._onValue = options.onValue ?? true;
        this._offValue = options.offValue ?? false;

        // Store min/max values explicitly
        const defaultMin = this.action.type === 'analog'
            ? this.action.minValue ?? Number.NEGATIVE_INFINITY
            : Number.NEGATIVE_INFINITY;
        this._min = options.min ?? defaultMin;

        const defaultMax = this.action.type === 'analog'
            ? this.action.maxValue ?? Number.POSITIVE_INFINITY
            : Number.POSITIVE_INFINITY;
        this._max = options.max ?? defaultMax;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Process input state, apply scaling/deadzone/clamping, and emit an action event if the value changed.
     *
     * @param state
     * @param eventBus
     */
    public process(state : InputState, eventBus : GameEventBus) : void
    {
        const rawValue = this.reader.getValue(state);
        if(rawValue === undefined) { return; }

        const numericValue = typeof rawValue === 'boolean' ? (rawValue ? 1.0 : 0.0) : rawValue;

        let value = this._deadzone > 0 && Math.abs(numericValue) < this._deadzone ? 0 : numericValue;
        value = ((this._invert ? -value : value) * this._scale) + this._offset;

        // Apply min/max clamping
        value = Math.max(this._min, Math.min(this._max, value));

        if(this._emitOnChange && this._lastValue === value) { return; }
        this._lastValue = value;

        eventBus.publish({
            type: `action:${ this.action.name }`,
            payload: {
                value,
                deviceId: this.deviceID,
                context: this.context,
            },
        });
    }

    /**
     * No-op for value bindings — they have no edge-detection state to reset.
     */
    public resetEdgeState(_state ?: InputState) : void
    {
        // Value bindings don't track edge state
    }

    /**
     * Returns a JSON-serializable representation of this value binding.
     */
    public toJSON() : ValueBindingDefinition
    {
        return {
            type: this.type,
            action: this.action.name,
            input: {
                deviceID: this.deviceID,
                ...this.reader.toJSON(),
            },
            context: this.context,
            options: this.options,
        };
    }
}

//----------------------------------------------------------------------------------------------------------------------
